import { useState } from 'react';
import { useT } from '@/store';
import { DEFECT_SEVERITIES, useReportDefect, useAllVehicles } from '@/hooks/useFleet';
import { Modal } from '@/components/ui/Overlay';
import { ErrorState } from '@/components/ui/Panel';

/**
 * Dispatcher-side defect entry — the office half of the driver app's report
 * form, posting to the same endpoint so a fault phoned in and one reported
 * from the cab are one kind of record.
 *
 * The vehicle picker is the only field the driver's version does not have:
 * a driver can only report against the truck they are sitting in, a
 * dispatcher reports against any of them.
 *
 * `critical_grounded` carries a warning line because it is not just a louder
 * severity — the backend flips the vehicle to 'in_service' and alerts the
 * fleet over Telegram. Whoever picks it should know they are stopping a
 * truck, not filing a note.
 */
export function DefectFormModal({ vehicleId: initialVehicleId, onClose }) {
  const t = useT();
  const { data: vehicles = [] } = useAllVehicles();
  const reportDefect = useReportDefect();

  const [vehicleId, setVehicleId] = useState(initialVehicleId ?? '');
  const [title, setTitle] = useState('');
  const [severity, setSeverity] = useState('medium');
  const [description, setDescription] = useState('');
  const [photo, setPhoto] = useState(null);

  const submit = () => {
    if (!vehicleId || !title.trim()) return;
    reportDefect.mutate(
      {
        vehicleId,
        title: title.trim(),
        severity,
        description: description.trim() || undefined,
        photo,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t('transportas.reportDefect')}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={submit}
            disabled={reportDefect.isPending || !vehicleId || !title.trim()}
          >
            {t('common.saveBtn')}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label={t('transportas.vehicleLabel')}>
          <select className="field" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
            <option value="">{t('transportas.pickVehicle')}</option>
            {vehicles.map((vehicle) => (
              <option key={vehicle.vehicle_id} value={vehicle.vehicle_id}>
                {vehicle.license_plate}
                {vehicle.make_model ? ` · ${vehicle.make_model}` : ''}
              </option>
            ))}
          </select>
        </Field>

        <Field label={t('transportas.defectTitleLabel')}>
          <input
            className="field"
            value={title}
            placeholder={t('transportas.defectTitlePh')}
            onChange={(e) => setTitle(e.target.value)}
          />
        </Field>

        <Field label={t('transportas.severityLabel')}>
          <select className="field" value={severity} onChange={(e) => setSeverity(e.target.value)}>
            {DEFECT_SEVERITIES.map((code) => (
              <option key={code} value={code}>
                {t(`transportas.severity.${code}`)}
              </option>
            ))}
          </select>
        </Field>

        {severity === 'critical_grounded' ? (
          <p className="rounded-[10px] border border-red/25 bg-red/8 px-3 py-2 text-[11px] leading-snug text-red-ink">
            {t('transportas.criticalHint')}
          </p>
        ) : null}

        <Field label={t('transportas.defectDescLabel')}>
          <textarea
            className="field min-h-[72px]"
            value={description}
            placeholder={t('transportas.defectDescPh')}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>

        <Field label={t('transportas.defectPhoto')}>
          <input
            className="field !py-1.5"
            type="file"
            accept="image/*"
            onChange={(e) => setPhoto(e.target.files?.[0] ?? null)}
          />
        </Field>

        {reportDefect.isError ? <ErrorState error={reportDefect.error} /> : null}
      </div>
    </Modal>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[10.5px] font-bold uppercase tracking-[0.03em] text-dim">{label}</span>
      {children}
    </label>
  );
}
